import React, { useEffect, useRef } from 'react';
import { Card } from 'flowbite-react';
import { Shape } from '../Types';
import { ellipsoidPoints, squarePoints, calculateResizedDimensions } from '../utils/shapeUtils';

interface PreviewCanvasProps {
  selectedImage: string;
  selectedShape: Shape;
  invertImage: boolean;
  maskSize?: number;
  numberOfPins?: number;
}

const MAX_DIMENSION = 300;

function invertCanvas(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const imageData = ctx.getImageData(0, 0, width, height);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i];
    data[i + 1] = 255 - data[i + 1];
    data[i + 2] = 255 - data[i + 2];
  }

  ctx.putImageData(imageData, 0, 0);
}

function overlayMaskedArea(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  shape: Shape,
  maskPercent: number,
): void {
  if (maskPercent >= 100) {
    return;
  }

  const scale = maskPercent / 100;
  const centerX = width / 2;
  const centerY = height / 2;
  const imageData = ctx.getImageData(0, 0, width, height);
  const { data } = imageData;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let insideMask = false;

      if (shape === 'rectangle') {
        const halfWidth = (width * scale) / 2;
        const halfHeight = (height * scale) / 2;
        insideMask = Math.abs(x - centerX) <= halfWidth && Math.abs(y - centerY) <= halfHeight;
      } else {
        const radiusX = (width / 2) * scale;
        const radiusY = (height / 2) * scale;
        const dx = (x - centerX) / radiusX;
        const dy = (y - centerY) / radiusY;
        insideMask = dx * dx + dy * dy <= 1;
      }

      if (!insideMask) {
        const pixelIdx = (y * width + x) * 4;
        data[pixelIdx] = Math.round(data[pixelIdx] * 0.7 + 251 * 0.3);
        data[pixelIdx + 1] = Math.round(data[pixelIdx + 1] * 0.7 + 146 * 0.3);
        data[pixelIdx + 2] = Math.round(data[pixelIdx + 2] * 0.7 + 60 * 0.3);
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

const PreviewCanvas: React.FC<PreviewCanvasProps> = ({
  selectedImage,
  selectedShape,
  invertImage,
  maskSize = 100,
  numberOfPins = 200,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const drawPinsPreview = (
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    shape: Shape,
    maskPercent: number,
    pinCount: number,
  ) => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const { width, height } = calculateResizedDimensions(image.width, image.height, MAX_DIMENSION);
    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    if (invertImage) {
      invertCanvas(ctx, width, height);
    }

    const points =
      shape === 'rectangle'
        ? squarePoints(width, height, pinCount)
        : ellipsoidPoints([width / 2, height / 2], width / 2, height / 2, pinCount);

    ctx.fillStyle = '#8b5cf6';
    points.forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, 2 * Math.PI);
      ctx.fill();
    });

    overlayMaskedArea(ctx, width, height, shape, maskPercent);
  };

  useEffect(() => {
    if (canvasRef.current && imageRef.current && imageRef.current.complete) {
      drawPinsPreview(canvasRef.current, imageRef.current, selectedShape, maskSize, numberOfPins);
    }
  }, [selectedImage, selectedShape, invertImage, maskSize, numberOfPins]);

  const handleImageLoad = () => {
    if (canvasRef.current && imageRef.current) {
      drawPinsPreview(canvasRef.current, imageRef.current, selectedShape, maskSize, numberOfPins);
    }
  };

  return (
    <div className="mb-8 text-center">
      <Card className="max-w-md mx-auto">
        <canvas
          ref={canvasRef}
          className="border border-gray-200 rounded shadow-sm w-full"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        <img
          ref={imageRef}
          src={selectedImage}
          alt="Selected image"
          className="hidden"
          onLoad={handleImageLoad}
        />
        <p className="mt-2 text-sm font-medium text-gray-700">Selected Image</p>
        <p className="text-xs text-gray-500">Purple dots show pin positions for {selectedShape} shape</p>
      </Card>
    </div>
  );
};

export default PreviewCanvas;
